import { useCallback, useEffect, useMemo, useState } from 'react'
import { MagnifyingGlass } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { PaginationBar, usePagination } from '@/components/Pagination'

// -----------------------------------------------------------------------------
// Tipos
// -----------------------------------------------------------------------------

type AppStatus =
  | 'sin_invitar'
  | 'invitado'
  | 'invite_expirado'
  | 'activo'
  | 'bloqueado'
  | 'desconocido'

type Asociado = {
  id: string
  no_asociado: string
  nombre_completo: string
  dpi: string
  telefono_e164: string | null
  email: string | null
  municipio: string | null
  departamento: string | null
  usuario_core: string
  profesion: string | null
  // NeoCore-only (editables desde el admin)
  email_app: string | null
  telefono_app: string | null
  notas_internas: string | null
  // auth
  auth_user_id: string | null
  auth_phone: string | null
  auth_last_sign_in_at: string | null
  auth_banned_until: string | null
  invite_id: string | null
  invite_sent_at: string | null
  invite_canal: string | null
  invite_contacto: string | null
  invite_expires_at: string | null
  productos_count: number
  solicitudes_abiertas: number
  app_status: AppStatus
}

type FilterId = 'all' | 'sin_invitar' | 'invitado' | 'activo' | 'solicitudes_abiertas'

const FILTERS: { id: FilterId; label: string }[] = [
  { id: 'sin_invitar', label: 'Sin cuenta app' },
  { id: 'invitado', label: 'Invitados pendientes' },
  { id: 'activo', label: 'Activos en app' },
  { id: 'solicitudes_abiertas', label: 'Con solicitudes abiertas' },
  { id: 'all', label: 'Todos' },
]

// -----------------------------------------------------------------------------
// Utilidades de formato
// -----------------------------------------------------------------------------

function maskDpi(dpi: string): string {
  if (dpi.length !== 13) return dpi
  return dpi.slice(0, 3) + '*******' + dpi.slice(-3)
}

/** Normaliza cualquier input de teléfono GT a E.164: +502XXXXXXXX. */
function normalizePhoneE164(raw: string | null | undefined): string | null {
  if (!raw) return null
  const trimmed = String(raw).trim()
  if (!trimmed) return null
  const digits = trimmed.replace(/\D/g, '')
  if (digits.length === 8) return `+502${digits}`
  if (digits.length === 11 && digits.startsWith('502')) return `+${digits}`
  if (digits.length === 12 && digits.startsWith('502')) return `+${digits}`
  if (/^\+\d{10,15}$/.test(trimmed)) return trimmed
  return null
}

/** Formato visual para teléfonos GT: +502 5865 8811. */
function formatPhonePretty(raw: string | null): string {
  if (!raw) return '—'
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('502')) {
    return `+502 ${digits.slice(3, 7)} ${digits.slice(7)}`
  }
  if (digits.length === 8) return `+502 ${digits.slice(0, 4)} ${digits.slice(4)}`
  return raw
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-GT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Guatemala',
  })
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  const d = Math.floor(ms / 86400000)
  if (d === 0) return 'hoy'
  if (d === 1) return 'ayer'
  if (d < 30) return `hace ${d} días`
  if (d < 365) return `hace ${Math.floor(d / 30)} meses`
  return `hace ${Math.floor(d / 365)} años`
}

// -----------------------------------------------------------------------------
// Página principal
// -----------------------------------------------------------------------------

export function AsociadosPage() {
  const [rows, setRows] = useState<Asociado[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<FilterId>('sin_invitar')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Asociado | null>(null)
  const [pinDialog, setPinDialog] = useState<{
    pin: string
    no_asociado: string
    nombre_completo: string
    contacto: string | null
  } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const { data, error: err } = await supabase.rpc('list_asociados_for_admin')
    if (err) {
      setError(err.message)
      setRows([])
    } else {
      const fresh = (data ?? []) as Asociado[]
      setRows(fresh)
      // Si hay un asociado seleccionado, sincronizar su copia en el drawer
      // con la version fresca de la DB (para que los edits se vean al instante).
      setSelected((curr) => {
        if (!curr) return curr
        return fresh.find((a) => a.id === curr.id) ?? curr
      })
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const filteredRows = useMemo(() => {
    let r = rows
    if (filter !== 'all') {
      if (filter === 'solicitudes_abiertas') {
        r = r.filter((x) => (x.solicitudes_abiertas ?? 0) > 0)
      } else {
        r = r.filter((x) => x.app_status === filter)
      }
    }
    const s = search.trim().toLowerCase()
    if (s) {
      r = r.filter((x) => x.nombre_completo.toLowerCase().includes(s))
    }
    return r
  }, [rows, filter, search])

  const pg = usePagination(filteredRows, 20)

  const counts = useMemo(() => {
    const base: Record<FilterId, number> = {
      all: rows.length,
      sin_invitar: 0,
      invitado: 0,
      activo: 0,
      solicitudes_abiertas: 0,
    }
    for (const r of rows) {
      if (r.app_status === 'sin_invitar') base.sin_invitar++
      if (r.app_status === 'invitado' || r.app_status === 'invite_expirado') base.invitado++
      if (r.app_status === 'activo') base.activo++
      if ((r.solicitudes_abiertas ?? 0) > 0) base.solicitudes_abiertas++
    }
    return base
  }, [rows])

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card" style={{ padding: 0 }}>
        <div
          style={{
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            borderBottom: '1px solid var(--n-100)',
          }}
        >
          <div className="filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                className={'chip' + (filter === f.id ? ' active' : '')}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
                <span style={{ opacity: 0.7, fontVariant: 'tabular-nums' }}>
                  {' · '}
                  {counts[f.id]}
                </span>
              </button>
            ))}
          </div>

          {/* Buscador con lupa, alineado a los chips sin el "gran vacío".
              Antes el .filters tenía flex:1 que empujaba el input al borde
              derecho; ahora ambos comparten gap natural. */}
          <div className="search-input-wrap" style={{ flex: '0 1 280px', minWidth: 200 }}>
            <MagnifyingGlass size={14} weight="bold" className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por nombre…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button
            className="btn btn-ghost"
            onClick={load}
            disabled={loading}
            style={{ padding: '6px 14px' }}
          >
            {loading ? 'Cargando…' : 'Recargar'}
          </button>
        </div>

        {error ? (
          <div className="error-banner" style={{ margin: 16 }}>
            {error}
          </div>
        ) : null}

        <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>No. Asociado</th>
                <th>Nombre</th>
                <th>DPI</th>
                <th>Teléfono</th>
                <th>Productos</th>
                <th>Estado app</th>
                <th>Última actualización</th>
                <th className="actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="empty">Cargando asociados…</div>
                  </td>
                </tr>
              ) : pg.pageItems.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="empty">
                      <div className="empty-title">Sin resultados</div>
                      <div className="empty-sub">
                        Ningún asociado coincide con el filtro o la búsqueda.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                pg.pageItems.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelected(r)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="mono">{r.no_asociado}</td>
                    <td>{r.nombre_completo}</td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {maskDpi(r.dpi)}
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {r.telefono_e164 ?? '—'}
                    </td>
                    <td className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {r.productos_count}
                    </td>
                    <td>
                      <AppStatusBadge status={r.app_status} />
                      {(r.solicitudes_abiertas ?? 0) > 0 ? (
                        <div style={{ fontSize: 11, color: 'var(--warning)', marginTop: 2 }}>
                          {r.solicitudes_abiertas} solicitud
                          {r.solicitudes_abiertas > 1 ? 'es' : ''}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {r.auth_last_sign_in_at ? (
                        <>
                          <div>Login: {formatRelative(r.auth_last_sign_in_at)}</div>
                        </>
                      ) : r.invite_sent_at ? (
                        <div className="muted">
                          Invitado {formatRelative(r.invite_sent_at)}
                        </div>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn btn-ghost"
                        onClick={() => setSelected(r)}
                        style={{ padding: '4px 10px', fontSize: 12 }}
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <PaginationBar state={pg} />
      </div>

      {/* Panel de detalle */}
      {selected ? (
        <DetailDrawer
          asociado={selected}
          onClose={() => setSelected(null)}
          onActionComplete={(res) => {
            void load()
            if (res && 'pin' in res && res.pin) {
              setPinDialog({
                pin: res.pin,
                no_asociado: res.no_asociado,
                nombre_completo: selected.nombre_completo,
                // Prioriza telefono_app (NeoCore, editable) sobre el del core
                contacto: selected.telefono_app ?? selected.telefono_e164,
              })
            }
          }}
        />
      ) : null}

      {/* Modal de PIN */}
      {pinDialog ? (
        <PinDialog
          pin={pinDialog.pin}
          noAsociado={pinDialog.no_asociado}
          nombreCompleto={pinDialog.nombre_completo}
          contacto={pinDialog.contacto}
          onClose={() => setPinDialog(null)}
        />
      ) : null}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Badge
// -----------------------------------------------------------------------------

function AppStatusBadge({ status }: { status: AppStatus }) {
  const map: Record<AppStatus, { label: string; color: string; bg: string }> = {
    sin_invitar: { label: 'Sin cuenta', color: 'var(--n-600)', bg: 'var(--n-100)' },
    invitado: { label: 'Invitado', color: 'var(--brand-700)', bg: 'var(--brand-50)' },
    invite_expirado: { label: 'Invite expirado', color: 'var(--warning)', bg: '#fef3c7' },
    activo: { label: 'Activo', color: 'var(--success)', bg: '#dcfce7' },
    bloqueado: { label: 'Bloqueado', color: 'var(--error)', bg: '#fee2e2' },
    desconocido: { label: '?', color: 'var(--n-500)', bg: 'var(--n-100)' },
  }
  const s = map[status]
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 500,
        color: s.color,
        background: s.bg,
      }}
    >
      {s.label}
    </span>
  )
}

// -----------------------------------------------------------------------------
// Drawer de detalle + acciones
// -----------------------------------------------------------------------------

type ActionResult = {
  ok?: boolean
  pin?: string
  no_asociado: string
  error?: string
  detail?: string
}

function DetailDrawer({
  asociado,
  onClose,
  onActionComplete,
}: {
  asociado: Asociado
  onClose: () => void
  onActionComplete: (res?: ActionResult) => void
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [showDpi, setShowDpi] = useState(false)

  // Modo edit — campos NeoCore-only (editables desde esta ficha)
  const [editing, setEditing] = useState(false)
  const [emailApp, setEmailApp] = useState(asociado.email_app ?? '')
  const [telefonoApp, setTelefonoApp] = useState(asociado.telefono_app ?? '')
  const [notasInternas, setNotasInternas] = useState(asociado.notas_internas ?? '')
  const [saving, setSaving] = useState(false)

  // Contacto preferido para WhatsApp: telefono_app si existe, si no telefono_e164
  const contactoPreferido = asociado.telefono_app ?? asociado.telefono_e164 ?? null

  const call = async (
    action: 'invite' | 'reset' | 'disable' | 'unlink',
    confirmMsg?: string,
  ) => {
    if (confirmMsg && !confirm(confirmMsg)) return
    setBusy(action)
    setErr(null)
    try {
      const { data, error } = await supabase.functions.invoke('admin-credentials', {
        body: {
          action,
          asociado_id: asociado.id,
          // Pasamos contacto preferido — prioriza telefono_app si lo editaste
          contacto: contactoPreferido,
        },
      })
      if (error) throw new Error(error.message)
      const res = data as ActionResult
      if (res.error) {
        // Mensajes amigables para los errores conocidos de la Edge Function
        if (res.error === 'telefono_ocupado') {
          throw new Error(res.detail ?? 'Ese teléfono ya está ocupado por otro asociado.')
        }
        if (res.error === 'phone_required') {
          throw new Error(res.detail ?? 'Capturá primero el teléfono del asociado.')
        }
        if (res.error === 'already_invited') {
          throw new Error(res.detail ?? 'El asociado ya tiene credencial. Usá Reset.')
        }
        throw new Error(`${res.error}: ${res.detail ?? ''}`)
      }
      onActionComplete(res)
      if (action !== 'invite' && action !== 'reset') {
        onClose()
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Error desconocido')
    } finally {
      setBusy(null)
    }
  }

  const saveEdits = async () => {
    setSaving(true)
    setErr(null)
    try {
      // Normalizamos el telefono a E.164 antes de guardar. Si el admin tipeó
      // "58658811", "+50258658811" o "502 5865 8811", todos terminan como
      // +50258658811 en DB.
      const normalizedPhone = telefonoApp
        ? normalizePhoneE164(telefonoApp) ?? telefonoApp
        : null
      if (telefonoApp && !normalizePhoneE164(telefonoApp)) {
        setErr(
          'Teléfono inválido. Usá 8 dígitos GT (p.ej. 58658811) o formato E.164 (+50258658811).',
        )
        setSaving(false)
        return
      }
      const { error } = await supabase.rpc('update_asociado_neocore', {
        p_asociado_id: asociado.id,
        p_email_app: emailApp || null,
        p_telefono_app: normalizedPhone,
        p_notas_internas: notasInternas || null,
      })
      if (error) {
        // Mensaje especial para el unique violation que definimos en la RPC
        if (/telefono_app_duplicado|duplicate key|unique constraint/i.test(error.message)) {
          throw new Error(
            'Ese teléfono ya está asignado a otro asociado. Usá un número distinto o editá al otro asociado primero.',
          )
        }
        throw error
      }
      setEditing(false)
      onActionComplete()
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  const cancelEdit = () => {
    setEmailApp(asociado.email_app ?? '')
    setTelefonoApp(asociado.telefono_app ?? '')
    setNotasInternas(asociado.notas_internas ?? '')
    setEditing(false)
    setErr(null)
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.28)',
        zIndex: 40,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(520px, 100%)',
          height: '100%',
          background: 'var(--surface)',
          overflowY: 'auto',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.08)',
          padding: 24,
          display: 'grid',
          gap: 16,
          gridAutoRows: 'max-content',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div>
            <div className="mono" style={{ fontSize: 12, color: 'var(--n-500)' }}>
              {asociado.no_asociado}
            </div>
            <h2 style={{ margin: '4px 0 6px', fontSize: 18 }}>{asociado.nombre_completo}</h2>
            <AppStatusBadge status={asociado.app_status} />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {!editing ? (
              <button
                className="btn btn-ghost"
                onClick={() => setEditing(true)}
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                Editar
              </button>
            ) : null}
            <button className="btn btn-ghost" onClick={onClose} style={{ padding: '4px 10px' }}>
              ✕
            </button>
          </div>
        </div>

        {/* Errores de acciones (no edit — ese tiene su propio banner contextual) */}
        {err && !editing ? <div className="error-banner">{err}</div> : null}

        {/* Datos del core — siempre read-only */}
        <section>
          <SectionTitle>Datos del core (solo lectura)</SectionTitle>
          <div style={{ color: 'var(--n-500)', fontSize: 11, marginBottom: 10, marginTop: -6 }}>
            Estos campos vienen del xlsx del core. Si hay que cambiarlos, editá en el core y
            re-importá.
          </div>
          <Field label="DPI">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="mono">{showDpi ? asociado.dpi : maskDpi(asociado.dpi)}</span>
              <button
                className="btn btn-ghost"
                onClick={() => setShowDpi((v) => !v)}
                style={{ padding: '2px 8px', fontSize: 11 }}
              >
                {showDpi ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
          </Field>
          <Field label="Teléfono (core)">
            <span className="mono">{formatPhonePretty(asociado.telefono_e164)}</span>
          </Field>
          {asociado.email ? (
            <Field label="Email (core)">
              <span className="mono">{asociado.email}</span>
            </Field>
          ) : null}
          <Field label="Municipio">{asociado.municipio ?? '—'}</Field>
          <Field label="Departamento">{asociado.departamento ?? '—'}</Field>
          <Field label="Profesión">{asociado.profesion ?? '—'}</Field>
          <Field label="Usuario core">
            <span className="mono">{asociado.usuario_core}</span>
          </Field>
        </section>

        {/* Campos NeoCore editables */}
        <section>
          <SectionTitle>Datos NeoCore (editables)</SectionTitle>
          {!editing ? (
            <>
              <Field label="Email app">
                {asociado.email_app ? (
                  <span className="mono">{asociado.email_app}</span>
                ) : (
                  <span className="muted">—</span>
                )}
              </Field>
              <Field label="Teléfono app">
                {asociado.telefono_app ? (
                  <span className="mono">{formatPhonePretty(asociado.telefono_app)}</span>
                ) : (
                  <span className="muted">— (usa el del core)</span>
                )}
              </Field>
              <Field label="Notas internas">
                {asociado.notas_internas ? (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{asociado.notas_internas}</div>
                ) : (
                  <span className="muted">—</span>
                )}
              </Field>
            </>
          ) : (
            <div style={{ display: 'grid', gap: 12, marginTop: 4 }}>
              <EditField
                label="Email app"
                hint="Email personal del asociado para comunicación de la app. Separado del email que viene del core."
              >
                <input
                  type="email"
                  value={emailApp}
                  onChange={(e) => setEmailApp(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  style={inputStyle}
                />
              </EditField>
              <EditField
                label="Teléfono app"
                hint="8 dígitos GT o formato E.164 (+502 seguido del número). Lo normalizamos al guardar. Se usa como login en la app y para WhatsApp."
              >
                <input
                  type="tel"
                  value={telefonoApp}
                  onChange={(e) => setTelefonoApp(e.target.value)}
                  placeholder="8 dígitos o +502…"
                  style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace' }}
                />
              </EditField>
              <EditField label="Notas internas" hint="Libre, solo visible para operadores.">
                <textarea
                  value={notasInternas}
                  onChange={(e) => setNotasInternas(e.target.value)}
                  rows={4}
                  placeholder="Visitó la sucursal, pidió información sobre plazo fijo…"
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                />
              </EditField>

              {/* Error JUSTO antes de los botones, asi siempre se ve cuando apretás guardar */}
              {err ? (
                <div className="error-banner" style={{ margin: 0 }}>
                  {err}
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-primary"
                  onClick={saveEdits}
                  disabled={saving}
                  style={{ flex: 1 }}
                >
                  {saving ? 'Guardando…' : 'Guardar cambios'}
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={cancelEdit}
                  disabled={saving}
                  style={{ flex: 1 }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Credencial */}
        <section>
          <SectionTitle>Credencial de la app</SectionTitle>
          <Field label="Estado">
            <AppStatusBadge status={asociado.app_status} />
          </Field>
          <Field label="Último login">
            {asociado.auth_last_sign_in_at ? (
              <>
                {formatDate(asociado.auth_last_sign_in_at)}{' '}
                <span className="muted">
                  ({formatRelative(asociado.auth_last_sign_in_at)})
                </span>
              </>
            ) : (
              '—'
            )}
          </Field>
          <Field label="Invitación enviada">
            {asociado.invite_sent_at ? (
              <>
                {formatDate(asociado.invite_sent_at)}
                {asociado.invite_canal ? (
                  <span className="muted"> · {asociado.invite_canal}</span>
                ) : null}
              </>
            ) : (
              '—'
            )}
          </Field>
        </section>

        {/* Acciones */}
        {!editing ? (
          <section>
            <SectionTitle>Acciones</SectionTitle>

            {!contactoPreferido ? (
              <div
                style={{
                  padding: '10px 12px',
                  background: 'var(--brand-50)',
                  border: '1px solid var(--brand-200)',
                  borderRadius: 8,
                  fontSize: 12.5,
                  color: 'var(--brand-800)',
                  marginBottom: 10,
                  lineHeight: 1.5,
                }}
              >
                El asociado necesita un <strong>teléfono válido</strong> para tener cuenta
                en la app. Tocá "Editar" arriba y capturá un{' '}
                <code>telefono_app</code> en formato GT (8 dígitos) o E.164.
              </div>
            ) : null}

            <div style={{ display: 'grid', gap: 8 }}>
              {asociado.app_status === 'sin_invitar' ||
              asociado.app_status === 'invite_expirado' ? (
                <button
                  className="btn btn-primary"
                  onClick={() => call('invite')}
                  disabled={busy !== null || !contactoPreferido}
                  title={
                    !contactoPreferido
                      ? 'Capturá un teléfono primero en el form de edit'
                      : undefined
                  }
                >
                  {busy === 'invite' ? 'Generando PIN…' : 'Invitar — generar PIN'}
                </button>
              ) : null}

              {asociado.app_status === 'invitado' ||
              asociado.app_status === 'activo' ||
              asociado.app_status === 'bloqueado' ? (
                <button
                  className="btn btn-ghost"
                  onClick={() =>
                    call('reset', '¿Generar un nuevo PIN? El anterior deja de funcionar.')
                  }
                  disabled={busy !== null}
                >
                  {busy === 'reset' ? 'Generando…' : 'Reenviar / Reset PIN'}
                </button>
              ) : null}

              {(asociado.app_status === 'activo' ||
                asociado.app_status === 'invitado') && (
                <button
                  className="btn btn-danger"
                  onClick={() =>
                    call(
                      'disable',
                      '¿Bloquear acceso a la app? El asociado no podrá entrar hasta que lo reactives con un reset.',
                    )
                  }
                  disabled={busy !== null}
                >
                  {busy === 'disable' ? 'Bloqueando…' : 'Bloquear acceso'}
                </button>
              )}

              {asociado.auth_user_id ? (
                <button
                  className="btn btn-danger"
                  onClick={() =>
                    call(
                      'unlink',
                      '¿Desvincular cuenta de app? Se borra el usuario de auth y se limpia el link. El asociado vuelve a "sin cuenta".',
                    )
                  }
                  disabled={busy !== null}
                >
                  {busy === 'unlink' ? 'Desvinculando…' : 'Desvincular (borrar cuenta app)'}
                </button>
              ) : null}
            </div>
          </section>
        ) : null}
      </aside>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid var(--n-200)',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 14,
  fontFamily: 'inherit',
}

function EditField({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 12, color: 'var(--n-700)', fontWeight: 500 }}>{label}</span>
      {children}
      {hint ? (
        <span style={{ fontSize: 11, color: 'var(--n-500)' }}>{hint}</span>
      ) : null}
    </label>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        color: 'var(--n-500)',
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '140px 1fr',
        gap: 8,
        padding: '6px 0',
        borderBottom: '1px solid var(--n-100)',
        fontSize: 13,
      }}
    >
      <div style={{ color: 'var(--n-500)' }}>{label}</div>
      <div>{children}</div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Modal del PIN generado
// -----------------------------------------------------------------------------

function PinDialog({
  pin,
  noAsociado,
  nombreCompleto,
  contacto,
  onClose,
}: {
  pin: string
  noAsociado: string
  nombreCompleto: string
  contacto: string | null
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  const [phone, setPhone] = useState(contacto ?? '')

  const copyPin = async () => {
    await navigator.clipboard.writeText(pin)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Title-case del primer nombre del asociado (el core los guarda en MAYUSCULAS)
  const firstName = (() => {
    const parts = nombreCompleto.trim().split(/\s+/)
    const first = parts[0] ?? ''
    return first ? first.charAt(0).toUpperCase() + first.slice(1).toLowerCase() : ''
  })()

  const digits = phone.replace(/\D/g, '')
  const message =
    `Hola ${firstName}, ya podés entrar a la app de neocoop.\n\n` +
    `Usá tu número de teléfono y este PIN: ${pin}\n\n` +
    `La primera vez que ingreses, la app te va a pedir crear tu contraseña.`
  const waHref = digits.length >= 8 ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}` : null

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 12,
          padding: 24,
          maxWidth: 480,
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16 }}>PIN generado</h3>
        <p style={{ color: 'var(--n-500)', fontSize: 13, margin: '6px 0 16px', lineHeight: 1.5 }}>
          Este PIN se muestra <strong>una sola vez</strong>. Compartilo con el asociado por
          WhatsApp. Si cerrás sin copiarlo, no vas a poder volver a verlo — tenés que generar
          uno nuevo con "Reset PIN".
        </p>

        <div
          style={{
            background: 'var(--bg)',
            border: '2px solid var(--brand-500)',
            borderRadius: 12,
            padding: 16,
            textAlign: 'center',
            margin: '12px 0 16px',
          }}
        >
          <div style={{ color: 'var(--n-500)', fontSize: 11, marginBottom: 6 }}>
            Usuario: <span className="mono">{noAsociado}</span>
          </div>
          <div
            className="mono"
            style={{
              fontSize: 32,
              letterSpacing: 6,
              fontWeight: 700,
              color: 'var(--brand-600)',
            }}
          >
            {pin}
          </div>
        </div>

        {/* Input de teléfono editable */}
        <label style={{ display: 'block', fontSize: 12, color: 'var(--n-500)', marginBottom: 4 }}>
          WhatsApp al que se envía
        </label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+50258658811"
          style={{
            width: '100%',
            border: '1px solid var(--n-200)',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 14,
            fontFamily: 'ui-monospace, monospace',
            marginBottom: 4,
          }}
        />
        <div style={{ fontSize: 11, color: 'var(--n-500)', marginBottom: 14 }}>
          {contacto
            ? `Default: teléfono del core (${contacto}). Editalo si vas a mandarlo por otra línea.`
            : 'Este asociado no tiene teléfono en el core — ponelo a mano.'}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost" onClick={copyPin} style={{ flex: 1 }}>
            {copied ? '✓ Copiado' : 'Copiar PIN'}
          </button>
          {waHref ? (
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary"
              style={{ flex: 1, textDecoration: 'none', textAlign: 'center' }}
            >
              Abrir WhatsApp
            </a>
          ) : (
            <button className="btn btn-primary" disabled style={{ flex: 1 }}>
              Abrir WhatsApp (falta teléfono)
            </button>
          )}
        </div>

        <button
          className="btn btn-ghost"
          onClick={onClose}
          style={{ marginTop: 8, width: '100%' }}
        >
          Cerrar
        </button>
      </div>
    </div>
  )
}
